import { IPostComment } from '_interfaces/postComment.interface';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { atom } from 'jotai';
import { atomFamily, atomWithObservable, atomWithStorage, loadable } from 'jotai/utils';
import { Observable } from 'rxjs';
import { auth, db } from 'utils/firebase';
import { selectedCompanyIdAtom } from './company';
import { selectedProjectIdAtom } from './projects';
import { createCrudAtoms } from './utils/crudFactory';
import { commentMatchesSearch } from './utils/searchUtils';

// Persistent cache for invoice data
export const postCommentsCacheAtom = atomWithStorage<IPostComment[]>('postCommentsCache', []);

// Real-time Firestore listener (only updates when active)
export const postCommentsAtom = atomFamily((parentPath: string) =>
  atomWithObservable<IPostComment[]>(
    (get) => {
      const companyId = get(selectedCompanyIdAtom);
      const projectId = get(selectedProjectIdAtom);

      // Pull a synchronous initial value from cache (or [] if not ready)
      const cached = get(postCommentsCacheAtom);
      const initial: IPostComment[] = cached instanceof Promise ? [] : cached;

      if (!companyId || !projectId) {
        return new Observable<IPostComment[]>((subscriber) => {
          // Emit synchronously to avoid suspension when Firestore is inactive
          subscriber.next(initial);
        });
      }

      const postCommentsQuery = query(
        collection(db, `${parentPath}/comments`),
        orderBy('createdAt', 'desc')
      );

      return new Observable<IPostComment[]>((subscriber) => {
        // Emit synchronously so first read never suspends
        subscriber.next(initial);

        const unsubscribe = onSnapshot(postCommentsQuery, (snapshot) => {
          const postCommentData: IPostComment[] = snapshot.docs.map((doc) =>
            IPostComment.fromDocumentData({ ...doc.data(), id: doc.id })
          );

          subscriber.next(postCommentData);
        });

        return () => {
          unsubscribe();
        };
      });
    },
    {
      initialValue: [],
    }
  )
);

export const filteredPostCommentsAtom = atomFamily(
  ({ parentPath, searchQuery }: { parentPath: string; searchQuery: string }) =>
    atom((get) => {
      const comments = get(postCommentsAtom(parentPath));

      if (!searchQuery.trim()) {
        return comments;
      }

      return comments.filter((comment) => commentMatchesSearch(comment, searchQuery));
    })
);

// Sync latest Firestore data into cache every time postCommentsAtom updates
export const postCommentsSyncAtom = atomFamily((parentPath: string) =>
  atom(null, async (get, set) => {
    const loaded = get(loadable(postCommentsAtom(parentPath)));
    if (loaded.state === 'hasData') {
      set(postCommentsCacheAtom, loaded.data);
    }
    // If loading or hasError, do nothing here to avoid suspending in a write
  })
);

export const togglePostCommentLikeAtom = atom(
  null,
  async (
    get,
    set,
    payload: {
      commentId: string;
      parentPath: string;
      userId: string;
    }
  ) => {
    // Check Firebase auth state first
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error('No Firebase user authenticated');
      throw new Error('User must be authenticated to like posts');
    }

    const { commentId, parentPath, userId } = payload;

    // Check what's in the cache
    const allPostComments = get(postCommentsAtom(parentPath));

    const postComment = allPostComments.find((p) => p.id === commentId);

    if (!postComment) {
      console.error('Post Comment not found with ID:', commentId);
      console.error('Error getting post comment from Firestore:', commentId);
      return;
    }

    const hasLiked = postComment.likes?.includes(userId) ?? false;
    const newlikes = hasLiked
      ? postComment.likes?.filter((uid) => uid !== userId)
      : [...(postComment.likes || []), userId];

    // Use direct Firestore update instead of createCrudAtoms
    try {
      await set(updatePostCommentAtom, { ...postComment, likes: newlikes });
    } catch (error) {
      console.error('Error updating Firestore:', error);
      throw error;
    }
  }
);

export const addPostCommentAtom = atom(
  null,
  async (
    get,
    set,
    params: {
      data: Partial<IPostComment>;
      parentPath: string;
      user?: { userId: string; displayName: string; avatarUrl: string };
    }
  ) => {
    const companyId = get(selectedCompanyIdAtom);
    const projectId = get(selectedProjectIdAtom);

    const { addAtom } = createCrudAtoms<IPostComment>(`${params.parentPath}/comments`);
    await set(addAtom, {
      ...IPostComment.empty(),
      ...params.data,
      companyId,
      projectId: projectId.toString(),
      parentPath: params.parentPath,
      ...(params.user && {
        createdBy: {
          userId: params.user.userId,
          displayName: params.user.displayName,
          avatarUrl: params.user.avatarUrl,
        },
      }),
    });
  }
);

export const updatePostCommentAtom = atom(
  null,
  async (get, set, comment: Partial<IPostComment>) => {
    const { updateAtom } = createCrudAtoms<IPostComment>(`${comment.parentPath}/comments`);
    await set(updateAtom, {
      id: comment.id,
      data: comment,
    });
  }
);

export const deletePostCommentAtom = atom(
  null,
  async (get, set, parentPath: string, commentId: string) => {
    const { deleteAtom } = createCrudAtoms<IPostComment>(`${parentPath}/comments`);
    await set(deleteAtom, commentId);
  }
);
